import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import './EReader.css';

const LINE_HEIGHTS = [{ value: 1.55, label: 'فاصله کم' }, { value: 1.85, label: 'فاصله متوسط' }, { value: 2.15, label: 'فاصله زیاد' }];
const DEFAULT_SETTINGS = { fontSize: 18, lineHeight: 1.85, theme: 'day', viewMode: 'single', wordLimit: 250, followSentence: true };
const SPEEDS = [0.5, 0.75, 1, 1.25, 1.5, 1.75, 2];

function loadSettings() { try { return { ...DEFAULT_SETTINGS, ...JSON.parse(localStorage.getItem('ereader-settings') || '{}'), viewMode: 'single' }; } catch { return DEFAULT_SETTINGS; } }
function formatTime(sec) { if (!Number.isFinite(sec)) return '00:00'; const s = Math.max(0, Math.floor(sec)); return `${String(Math.floor(s / 60)).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`; }
function wordsFromText(text) { return String(text || '').split(/(\s+)/).filter(Boolean); }
function cleanWord(text) { return String(text || '').toLowerCase().replace(/[^\p{L}\p{N}'-]/gu, ''); }

function sentenceRanges(text) {
  const out = [];
  const re = /[^.!?]+(?:[.!?]+|$)/g;
  let m;
  while ((m = re.exec(String(text || '')))) {
    const value = m[0];
    const words = value.split(/\s+/).filter(Boolean).length;
    if (words) out.push({ text: value.trim(), startWord: out.length ? 0 : 0, wordCount: words });
  }
  return out;
}


function getGoogleDriveEmbedUrl(url) {
  const value = String(url || '').trim();
  const file = value.match(/drive\.google\.com\/file\/d\/([^/]+)/i);
  if (file?.[1]) return `https://drive.google.com/file/d/${file[1]}/preview`;
  const id = value.match(/[?&]id=([^&]+)/i);
  if (id?.[1] && value.includes('drive.google.com')) return `https://drive.google.com/file/d/${id[1]}/preview`;
  return value;
}

function AudioSyncPlayer({ src, storageKey, words, onTime, onDuration, onWordSelect, timingUrl, selectedWordIndex, onEnded }) {
  const audioRef = useRef(null);
  const objectUrlRef = useRef('');
  const [localSrc, setLocalSrc] = useState('');
  const [customSrc, setCustomSrc] = useState('');
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [rate, setRate] = useState(1);
  const [volume, setVolume] = useState(1);
  const [muted, setMuted] = useState(false);
  const [error, setError] = useState(false);
  const [timings, setTimings] = useState([]);
  const [timingSource, setTimingSource] = useState('estimated');
  const [fileName, setFileName] = useState('');
  const [urlInput, setUrlInput] = useState(src || '');
  const audioSource = localSrc || customSrc || src || '';
  const isGoogleDrive = !localSrc && !customSrc && /drive\.google\.com/i.test(audioSource);
  const storage = storageKey ? `audio-progress-${storageKey}` : null;

  useEffect(() => () => { if (objectUrlRef.current) URL.revokeObjectURL(objectUrlRef.current); }, []);
  useEffect(() => { setUrlInput(src || ''); setCustomSrc(''); setError(false); setPlaying(false); setCurrentTime(0); setDuration(0); }, [src]);
  useEffect(() => { if (selectedWordIndex >= 0 && timings[selectedWordIndex]) seekTo(timings[selectedWordIndex].start); }, [selectedWordIndex, timings]);

  const buildEstimatedTimings = useCallback((dur) => {
    if (!dur || !words.length) return;
    const count = words.length;
    const step = dur / count;
    setTimings(words.map((_, i) => ({ start: i * step, end: Math.min(dur, (i + 1) * step) })));
    setTimingSource('estimated');
  }, [words]);

  const loadTimingFile = useCallback(async (audioUrl, dur) => {
    if (!audioUrl || !words.length) { setTimings([]); return; }
    const candidates = [];
    if (timingUrl) candidates.push(timingUrl);
    try { candidates.push(`${audioUrl.replace(/\.[^/.?#]+([?#].*)?$/, '')}.json`); } catch {}
    candidates.push(`${audioUrl}.json`);
    for (const candidate of candidates) {
      try {
        let data = null;
        const trimmedCandidate = String(candidate || '').trim();
        if (trimmedCandidate.startsWith('{') || trimmedCandidate.startsWith('[')) {
          data = JSON.parse(trimmedCandidate);
        } else {
          const response = await fetch(candidate, { cache: 'no-store' });
          if (!response.ok) continue;
          data = await response.json();
        }
        const source = Array.isArray(data) ? data : Array.isArray(data.words) ? data.words : Array.isArray(data.segments) ? data.segments : [];
        if (!source.length) continue;
        const cleaned = source.map((item) => ({ text: item.text || '', start: Number(item.start), end: Number(item.end) })).filter(x => Number.isFinite(x.start) && Number.isFinite(x.end));
        if (!cleaned.length) continue;
        if (cleaned.length === words.length) {
          setTimings(cleaned); setTimingSource('json'); return;
        }
        // If the JSON contains sentence-level timestamps, distribute each segment across its words.
        const result = [];
        const plainWords = words.map(cleanWord).filter(Boolean);
        let cursor = 0;
        cleaned.forEach(seg => {
          const segWords = String(seg.text).split(/\s+/).map(cleanWord).filter(Boolean);
          let count = Math.max(1, segWords.length);
          if (!segWords.length) count = 1;
          const step = Math.max(0.01, (seg.end - seg.start) / count);
          for (let i = 0; i < count && cursor < plainWords.length; i += 1) {
            result[cursor] = { start: seg.start + i * step, end: Math.min(seg.end, seg.start + (i + 1) * step) };
            cursor += 1;
          }
        });
        if (result.length >= Math.max(1, Math.floor(words.length * 0.75))) {
          while (result.length < words.length) {
            const last = result[result.length - 1] || { start: 0, end: dur };
            const remaining = words.length - result.length;
            const step = Math.max(0.01, (dur - last.end) / Math.max(1, remaining));
            result.push({ start: last.end, end: Math.min(dur, last.end + step) });
          }
          setTimings(result); setTimingSource('json'); return;
        }
      } catch { /* fallback to estimate */ }
    }
    buildEstimatedTimings(dur);
  }, [buildEstimatedTimings, timingUrl, words]);

  const handleMetadata = () => {
    const audio = audioRef.current; if (!audio) return;
    const dur = Number.isFinite(audio.duration) ? audio.duration : 0;
    setDuration(dur); onDuration?.(dur);
    if (storage) {
      try { const saved = Number(localStorage.getItem(storage)); if (saved > 0 && saved < dur - 2) audio.currentTime = saved; } catch {}
    }
    loadTimingFile(audioSource, dur);
  };

  const handleTime = () => {
    const audio = audioRef.current; if (!audio) return;
    let idx = -1;
    for (let i = 0; i < timings.length; i += 1) { if (audio.currentTime >= timings[i].start && audio.currentTime <= timings[i].end) { idx = i; break; } if (audio.currentTime >= timings[i].start) idx = i; }
    setCurrentTime(audio.currentTime); onTime?.(audio.currentTime, idx);
    if (storage) { try { localStorage.setItem(storage, String(audio.currentTime)); } catch {} }
  };

  const togglePlay = () => {
    const audio = audioRef.current; if (!audio) return;
    if (playing) audio.pause(); else audio.play().catch(() => setError(true));
  };
  const seek = (delta) => { if (audioRef.current) audioRef.current.currentTime = Math.max(0, Math.min(duration, audioRef.current.currentTime + delta)); };
  const stopAudio = () => { if (audioRef.current) { audioRef.current.pause(); audioRef.current.currentTime = 0; setCurrentTime(0); } };
  const seekTo = (time) => { if (audioRef.current && Number.isFinite(time)) { audioRef.current.currentTime = Math.max(0, Math.min(duration, time)); setCurrentTime(audioRef.current.currentTime); } };
  const handleUpload = (e) => { const file = e.target.files?.[0]; if (!file) return; if (objectUrlRef.current) URL.revokeObjectURL(objectUrlRef.current); objectUrlRef.current = URL.createObjectURL(file); setLocalSrc(objectUrlRef.current); setFileName(file.name); setUrlInput(file.name); setError(false); };
  const applyUrl = () => { const next = urlInput.trim(); if (!next) return; setLocalSrc(''); setCustomSrc(next); setFileName(next.split('/').pop()?.split('?')[0] || 'فایل صوتی'); setError(false); };
  const displaySource = audioSource;
  const progress = duration ? (currentTime / duration) * 100 : 0;

  return (
    <div className={`ereader-audio ${isGoogleDrive ? 'drive-audio' : ''}`}>
      {isGoogleDrive ? (
        <iframe className="google-drive-audio-frame" src={getGoogleDriveEmbedUrl(audioSource)} title="پخش صوت گوگل درایو" allow="autoplay" frameBorder="0" />
      ) : (
        <audio ref={audioRef} src={displaySource} preload="metadata" onLoadedMetadata={handleMetadata} onTimeUpdate={handleTime} onPlay={() => setPlaying(true)} onPause={() => setPlaying(false)} onEnded={() => { setPlaying(false); if (storage) { try { localStorage.removeItem(storage); } catch {} } onEnded?.(); }} onError={() => setError(true)} />
      )}
      <div className="audio-topline">
        <div className="audio-title"><strong>🎧 {fileName || (displaySource ? 'کتاب صوتی' : 'فایل صوتی')}</strong><span>{displaySource ? (timingSource === 'json' ? 'هم‌خوانی دقیق' : 'هم‌خوانی تخمینی') : 'بدون فایل صوتی'}</span></div>
        <details className="audio-settings">
          <summary>⚙</summary>
          <div className="audio-source-actions"><label className="upload-audio-btn">⬆ آپلود<input type="file" accept="audio/mpeg,audio/wav,audio/ogg,audio/*" onChange={handleUpload} /></label><input className="audio-url-input" value={urlInput} onChange={e => setUrlInput(e.target.value)} placeholder="لینک MP3 / WAV / OGG" /><button type="button" onClick={applyUrl}>اعمال</button></div>
        </details>
      </div>
      <div className="audio-drive-note">{isGoogleDrive ? 'پخش‌کننده Google Drive' : ''}</div>
      <div className="audio-wave" aria-hidden="true">{Array.from({ length: 42 }).map((_, i) => <i key={i} className={playing ? 'wave-on' : ''} style={{ '--i': i }} />)}</div>
      {error && <div className="audio-error">⚠️ فایل صوتی قابل پخش نیست یا لینک مستقیم نیست.</div>}
      <div className="audio-seek"><span>{formatTime(currentTime)}</span><input type="range" min="0" max={duration || 0} step="0.1" value={currentTime} onChange={e => seekTo(Number(e.target.value))} style={{ '--audio-progress': `${progress}%` }} /><span>{formatTime(duration)}</span></div>
      <div className="audio-controls">
        <select value={rate} onChange={e => { const r = Number(e.target.value); setRate(r); if (audioRef.current) audioRef.current.playbackRate = r; }} aria-label="سرعت پخش">{SPEEDS.map(s => <option key={s} value={s}>{s}x</option>)}</select>
        <button type="button" onClick={() => seek(-10)}>↶ ۱۰</button><button type="button" className="audio-play" onClick={togglePlay}>{playing ? 'Ⅱ' : '▶'}</button><button type="button" onClick={stopAudio}>■</button><button type="button" onClick={() => seek(10)}>۱۰ ↷</button>
        <button type="button" onClick={() => { setMuted(!muted); if (audioRef.current) audioRef.current.muted = !muted; }}>{muted ? '🔇' : '🔊'}</button>
        <input className="volume-range" type="range" min="0" max="1" step="0.01" value={muted ? 0 : volume} onChange={e => { const v = Number(e.target.value); setVolume(v); setMuted(false); if (audioRef.current) { audioRef.current.volume = v; audioRef.current.muted = false; } }} />
      </div>
      <div className="audio-file-info"><span>نام: {fileName || (displaySource ? (displaySource.split('/').pop()?.split('?')[0] || 'فایل صوتی') : '—')}</span><span>فرمت: {displaySource ? (displaySource.match(/\.(mp3|wav|ogg)(?:\?|$)/i)?.[1]?.toUpperCase() || 'AUDIO') : '—'}</span><span>مدت: {formatTime(duration)}</span><span>کیفیت: {displaySource ? 'وابسته به فایل' : '—'}</span></div>
      {timings.length > 0 && onWordSelect && <div className="audio-sync-hint">💡 روی هر کلمه بزنید تا صدا دقیقاً از همان نقطه پخش شود.</div>}
    </div>
  );
}

export default function EReader({ onBack, storageKey, content, formatText, emptyMessage, bookTitle, chapterTitle, audioSrc, timingUrl, onWordSelect, notes = [], totalBookPages = 0, selectedNote, onNoteSelect, isAdmin = false, onAddNote, onDeleteNote, showNoteForm = false, setShowNoteForm, newNote, setNewNote, onEdit, editing = false, editTitle = '', editContent = '', editAudioLink = '', setEditTitle, setEditContent, setEditAudioLink, onSaveEdit, onCancelEdit, onEnded, onAudioTime, editTimingLink = '', setEditTimingLink, onFollowSentenceChange, onNextNote }) {
  const [settings, setSettings] = useState(loadSettings);
  const [pages, setPages] = useState([]);
  const [pageIndex, setPageIndex] = useState(0);
  const [flipping, setFlipping] = useState('');
  const [resumePage, setResumePage] = useState(null);
  const [activeWord, setActiveWord] = useState(-1);
  const [clickedWordIndex, setClickedWordIndex] = useState(-1);
  const [notesOpen, setNotesOpen] = useState(false);
  const viewportRef = useRef(null);
  const initializedKeyRef = useRef(null);

  const paragraphs = useMemo(() => String(content || '').split(/\n+/).map(p => formatText ? formatText(p) : p).filter(Boolean), [content, formatText]);
  const tokens = useMemo(() => paragraphs.flatMap((p, pIndex) => wordsFromText(p).map((text, localIndex) => ({ text, pIndex, localIndex }))), [paragraphs]);
  const tokenWordIndices = useMemo(() => {
    const map = []; let word = 0;
    paragraphs.forEach((p, pIndex) => wordsFromText(p).forEach((text, localIndex) => { if (/\S/.test(text)) { map.push({ pIndex, localIndex, globalIndex: word }); word += 1; } }));
    return map;
  }, [paragraphs]);
  const wordTokens = useMemo(() => tokenWordIndices.map(x => tokens.find(t => t.pIndex === x.pIndex && t.localIndex === x.localIndex)?.text || ''), [tokenWordIndices, tokens]);

  const sentences = useMemo(() => {
    const all = [];
    let globalWord = 0;
    paragraphs.forEach((p, pIndex) => {
      sentenceRanges(p).forEach(seg => {
        const count = seg.wordCount;
        all.push({ text: seg.text, startWord: globalWord, endWord: globalWord + count, pIndex });
        globalWord += count;
      });
    });
    return all;
  }, [paragraphs]);
  const [activeSentence, setActiveSentence] = useState(-1);

  useEffect(() => { try { localStorage.setItem('ereader-settings', JSON.stringify(settings)); } catch {} }, [settings]);
  useEffect(() => { setPageIndex(0); setActiveWord(-1); }, [content, storageKey]);

  // Pagination is intentionally word-based so every physical page is a single page
  // and the amount of text is predictable. Paragraphs longer than the limit are
  // split without losing spaces/punctuation.
  const paginateByWords = useCallback(() => {
    const limit = Math.max(50, Math.min(1000, Number(settings.wordLimit) || 250));
    if (!paragraphs.length) { setPages([]); return; }

    const result = [];
    let current = [];
    let wordCount = 0;
    let globalWord = 0;

    paragraphs.forEach((paragraph, pIndex) => {
      const parts = wordsFromText(paragraph);
      let segment = '';
      let segmentStart = globalWord;

      const flushSegment = () => {
        if (!segment) return;
        current.push({ pIndex, text: segment, globalStart: segmentStart });
        segment = '';
      };

      parts.forEach((part) => {
        const isWord = /\S/.test(part);
        if (isWord && wordCount >= limit) {
          flushSegment();
          if (current.length) result.push(current);
          current = [];
          wordCount = 0;
          segmentStart = globalWord;
        }
        segment += part;
        if (isWord) { wordCount += 1; globalWord += 1; }
      });

      flushSegment();
    });

    if (current.length) result.push(current);
    setPages(result);
  }, [paragraphs, settings.wordLimit]);

  useEffect(() => { paginateByWords(); }, [paginateByWords]);
  useEffect(() => { if (pages.length && pageIndex >= pages.length) setPageIndex(pages.length - 1); }, [pages.length, pageIndex]);

  useEffect(() => {
    if (!storageKey || !pages.length || initializedKeyRef.current === storageKey) return;
    initializedKeyRef.current = storageKey;
    try { const saved = JSON.parse(localStorage.getItem(`reader-progress-${storageKey}`) || 'null'); if (saved?.page > 0 && saved.page < pages.length) setResumePage(saved.page); } catch {}
  }, [storageKey, pages.length]);
  useEffect(() => { if (storageKey && pages.length) { try { localStorage.setItem(`reader-progress-${storageKey}`, JSON.stringify({ page: pageIndex, total: pages.length, savedAt: Date.now() })); } catch {} } }, [storageKey, pageIndex, pages.length]);
  const goTo = (idx, dir) => {
    if (!pages.length) return;
    const next = Math.max(0, Math.min(pages.length - 1, idx));
    if (next === pageIndex) return;
    setFlipping(dir);
    // Change the page immediately so both navigation buttons always respond.
    setPageIndex(next);
    window.setTimeout(() => setFlipping(''), 360);
  };
  const goNext = () => { if (pageIndex >= pages.length - 1) { onNextNote?.(); return; } goTo(pageIndex + 1, 'next'); }
  const goPrev = () => goTo(pageIndex - 1, 'prev');
  useEffect(() => { const fn = e => { if (['INPUT','TEXTAREA','SELECT'].includes(e.target?.tagName)) return; if (e.key === 'ArrowRight') goNext(); if (e.key === 'ArrowLeft') goPrev(); }; window.addEventListener('keydown', fn); return () => window.removeEventListener('keydown', fn); });


  useEffect(() => {
    if (activeWord < 0) return;
    const found = sentences.findIndex(x => activeWord >= x.startWord && activeWord < x.endWord);
    setActiveSentence(found);
  }, [activeWord, sentences]);

  useEffect(() => {
    if (!settings.followSentence || activeSentence < 0) return;
    const id = window.setTimeout(() => {
      const el = document.querySelector('.book-page-single .ereader-word.sentence-active');
      el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 40);
    return () => window.clearTimeout(id);
  }, [activeSentence, pageIndex, settings.followSentence]);

  useEffect(() => {
    if (activeWord < 0 || !pages.length) return;
    const targetPage = pages.findIndex(page => page.some(block => {
      const count = wordsFromText(block.text).filter(x => /\S/.test(x)).length;
      return activeWord >= block.globalStart && activeWord < block.globalStart + count;
    }));
    if (targetPage >= 0 && targetPage !== pageIndex) setPageIndex(targetPage);
  }, [activeWord, pages, pageIndex]);

  const readerStyle = { fontSize: `${settings.fontSize}px`, lineHeight: settings.lineHeight };
  const progressPct = pages.length ? Math.round(((pageIndex + 1) / pages.length) * 100) : 0;
  const currentPageBlocks = pages[pageIndex] || [];

  const renderTextBlock = (block, blockIndex) => {
    const parts = wordsFromText(block.text);
    let globalWord = block.globalStart;
    const paragraphHasActiveSentence = parts.some(part => {
      if (!/\S/.test(part)) return false;
      const idx = globalWord;
      globalWord += 1;
      const sentenceIndex = sentences.findIndex(x => idx >= x.startWord && idx < x.endWord);
      return activeSentence === sentenceIndex;
    });
    globalWord = block.globalStart;
    return <p key={`${block.pIndex}-${blockIndex}`} className={paragraphHasActiveSentence ? 'sentence-container-active' : ''}>
      {parts.map((part, i) => {
        if (!/\S/.test(part)) return <React.Fragment key={i}>{part}</React.Fragment>;
        const idx = globalWord++;
        const sentenceIndex = sentences.findIndex(x => idx >= x.startWord && idx < x.endWord);
        const isSentenceActive = activeSentence === sentenceIndex;
        return <button type="button" key={`${block.pIndex}-${blockIndex}-${i}`}
          className={`ereader-word ${activeWord === idx ? 'is-current' : ''}`}
          onClick={() => { setActiveWord(idx); setClickedWordIndex(idx); onWordSelect?.(part.replace(/[^\p{L}\p{N}'-]/gu, '')); }}>
          {part}
        </button>;
      })}
    </p>;
  };

  const renderPage = () => currentPageBlocks.map(renderTextBlock);

  const handleSelection = () => { const selected = window.getSelection?.()?.toString().trim(); if (selected && selected.length <= 80) onWordSelect?.(selected.split(/\s+/)[0].replace(/[^\p{L}\p{N}'-]/gu, '')); };

  if (!paragraphs.length) return <div className={`ereader ereader-theme-${settings.theme}`}><div className="ereader-empty">{emptyMessage || 'محتوایی برای نمایش وجود ندارد.'}</div></div>;

  return <div className={`ereader ereader-theme-${settings.theme}`}>
    <div className="ereader-toolbar">
      <button type="button" className="toolbar-icon reader-back-button" onClick={onBack} title="بازگشت به خلاصه کتاب‌ها">←</button>
      <button type="button" className={`toolbar-icon notes-toggle ${notesOpen ? 'active' : ''}`} onClick={() => setNotesOpen(v => !v)} title="باز کردن کشوی Notes"><span className="notes-drawer-handle" aria-hidden="true"><i></i><i></i><i></i></span><span>Notes</span></button>
      <div className="ereader-toolbar-group"><button type="button" onClick={() => setSettings(s => ({ ...s, fontSize: Math.max(13, s.fontSize - 1) }))}>A−</button><span>{settings.fontSize}</span><button type="button" onClick={() => setSettings(s => ({ ...s, fontSize: Math.min(32, s.fontSize + 1) }))}>A+</button></div>
      <select className="ereader-select" value={settings.lineHeight} onChange={e => setSettings(s => ({ ...s, lineHeight: Number(e.target.value) }))}>{LINE_HEIGHTS.map(x => <option key={x.value} value={x.value}>{x.label}</option>)}</select>
      <div className="ereader-toolbar-group"><button type="button" className={settings.theme === 'day' ? 'active' : ''} onClick={() => setSettings(s => ({ ...s, theme: 'day' }))}>☀</button><button type="button" className={settings.theme === 'sepia' ? 'active' : ''} onClick={() => setSettings(s => ({ ...s, theme: 'sepia' }))}>◐</button><button type="button" className={settings.theme === 'night' ? 'active' : ''} onClick={() => setSettings(s => ({ ...s, theme: 'night' }))}>☾</button></div>
      <label className="word-limit-control" title="حداکثر تعداد کلمه در هر صفحه"><span>کلمه/صفحه</span><input type="number" min="50" max="1000" step="10" value={settings.wordLimit} onChange={e => setSettings(s => ({ ...s, wordLimit: Math.max(50, Math.min(1000, Number(e.target.value) || 250)) }))} /></label>
      <button type="button" className={`toolbar-icon follow-toggle ${settings.followSentence ? 'active' : ''}`} onClick={() => { const next = !settings.followSentence; setSettings(s => ({ ...s, followSentence: next })); onFollowSentenceChange?.(next); }} title="دنبال کردن جمله">{settings.followSentence ? '🟨' : '⬜'} دنبال‌کردن جمله</button>
      <div className="ereader-toolbar-spacer" />
      {isAdmin && <button type="button" className="toolbar-icon" onClick={editing ? onCancelEdit : onEdit}>{editing ? '×' : '✏️'}</button>}
    </div>

    {notesOpen && <>
      <div className="reader-drawer-backdrop" onClick={() => setNotesOpen(false)} />
      <aside className="reader-notes-drawer" aria-label="Notes">
        <div className="reader-notes-header"><div><span>CHIKHISE</span><h3>📝 Notes</h3></div><button type="button" onClick={() => setNotesOpen(false)}>×</button></div>
        {isAdmin && <button type="button" className="reader-add-note-btn" onClick={() => setShowNoteForm?.(v => !v)}>+ Add Note</button>}
        {showNoteForm && isAdmin && <form onSubmit={onAddNote} className="reader-add-note-form">
          <input type="number" placeholder="Note Number *" value={newNote?.noteNumber || ''} onChange={e => setNewNote?.({...newNote, noteNumber: e.target.value})} required />
          <input type="text" placeholder="Note Title" value={newNote?.title || ''} onChange={e => setNewNote?.({...newNote, title: e.target.value})} />
          <textarea placeholder="Note Content" rows="4" value={newNote?.content || ''} onChange={e => setNewNote?.({...newNote, content: e.target.value})} />
          <input type="number" placeholder="Order" value={newNote?.order || ''} onChange={e => setNewNote?.({...newNote, order: e.target.value})} />
          <input type="url" placeholder="🎵 Audio link" value={newNote?.audioLink || ''} onChange={e => setNewNote?.({...newNote, audioLink: e.target.value})} />
          <textarea placeholder="⏱ لینک JSON تایم‌کد یا خود JSON (اختیاری)" rows="3" value={newNote?.timingLink || ''} onChange={e => setNewNote?.({...newNote, timingLink: e.target.value})} />
          <div><button type="submit">Save</button><button type="button" onClick={() => setShowNoteForm?.(false)}>Cancel</button></div>
        </form>}
        <div className="reader-notes-list">
          {notes.map((note, index) => {
            const locked = !isAdmin && index > 0;
            return <button type="button" key={note._id || index} className={`reader-note-item ${selectedNote?._id === note._id ? 'active' : ''} ${locked ? 'locked' : ''}`} onClick={() => { if (!locked) { onNoteSelect?.(note, index); setNotesOpen(false); } }}>
              <span><b>Note {note.noteNumber}</b><em>{note.title || `Note ${note.noteNumber}`}</em></span><small>{locked ? '🔒' : note.audioLink ? '🎵' : '›'}</small>
              {isAdmin && <i onClick={(e) => { e.stopPropagation(); onDeleteNote?.(note._id); }}>🗑️</i>}
            </button>;
          })}
          {!notes.length && <div className="reader-no-notes">No notes yet.</div>}
        </div>
      </aside>
    </>}

    {resumePage !== null && <div className="ereader-resume"><span>📍 آخرین مطالعه شما: صفحه {resumePage + 1}</span><button type="button" onClick={() => { setPageIndex(resumePage); setResumePage(null); }}>ادامه مطالعه</button><button type="button" onClick={() => setResumePage(null)}>بستن</button></div>}

    {editing ? <div className="reader-edit-area">
      <input type="text" value={editTitle} onChange={e => setEditTitle?.(e.target.value)} placeholder="Note Title" />
      <textarea value={editContent} onChange={e => setEditContent?.(e.target.value)} rows={18} placeholder="Write note content here..." />
      <input type="url" value={editAudioLink || ''} onChange={e => setEditAudioLink?.(e.target.value)} placeholder="https://example.com/audio.mp3" />
      <textarea value={editTimingLink || ''} onChange={e => setEditTimingLink?.(e.target.value)} rows="3" placeholder="⏱ لینک timing.json یا خود JSON تایم‌کد" />
      <div><button type="button" onClick={onSaveEdit}>Save</button><button type="button" onClick={onCancelEdit}>Cancel</button></div>
    </div> : <>
      <div className={`ereader-stage ${flipping ? `is-flipping-${flipping}` : ''}`}>
        <div className="book-shadow" />
        <div className="book-spread" ref={viewportRef} onMouseUp={handleSelection}>
          <article className="book-page book-page-single" style={readerStyle}>
            <header><span>{bookTitle || 'چکیده‌سرا'}</span><b>{chapterTitle || 'Chapter'}</b></header>
            <div className="page-content">{renderPage()}</div>
            <footer><strong>{pageIndex + 1}</strong></footer>
          </article>
        </div>
        <div className="page-corner next-corner" onClick={goNext} aria-label="صفحه بعد">›</div>
        <div className="page-corner prev-corner" onClick={goPrev} aria-label="صفحه قبل">‹</div>
      </div>
      <div className="ereader-footer"><button type="button" onClick={goPrev} disabled={pageIndex === 0}>← قبلی</button><div className="progress-wrap"><div className="progress-meta"><span>صفحه {pages.length ? pageIndex + 1 : 0} از {totalBookPages || pages.length} · {Math.min(Number(settings.wordLimit) || 250, currentPageBlocks.reduce((n, b) => n + wordsFromText(b.text).filter(x => /\S/.test(x)).length, 0))} کلمه</span><strong>{progressPct}%</strong></div><div className="progress-line"><i style={{ width: `${progressPct}%` }} /></div></div><button type="button" onClick={goNext} disabled={pageIndex >= pages.length - 1}>بعدی →</button></div>
    </>}

    {audioSrc && <AudioSyncPlayer src={audioSrc} timingUrl={timingUrl} storageKey={storageKey} words={wordTokens} selectedWordIndex={clickedWordIndex} onTime={(time, index) => { setActiveWord(index); onAudioTime?.(time, index); }} onDuration={() => {}} onWordSelect={(word) => onWordSelect?.(word)} onEnded={onEnded} />}
  </div>;
}
